import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Alert, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  addDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaEye, FaArrowRight } from 'react-icons/fa';

const PendingAssignments = () => {
  const { currentUser, userRole } = useAuth();
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (userRole !== 'dosen') {
      setError("Halaman ini hanya untuk dosen");
      setLoading(false);
      return;
    }
    
    fetchPendingAssignments();
  }, [currentUser, userRole]);
  
  const fetchPendingAssignments = async () => {
    try {
      setLoading(true);
      
      // Get tugas akhir where this dosen is assigned as pembimbing
      const taQuery = query(
        collection(db, "tugasAkhir"),
        where("pembimbingId", "array-contains", currentUser.uid),
        where("status", "==", "approved")
      );
      
      const taSnapshot = await getDocs(taQuery);
      const pendingData = [];
      
      for (const taDoc of taSnapshot.docs) {
        const taData = taDoc.data();
        taData.id = taDoc.id;
        
        // Check if this assignment has been accepted or rejected by the dosen
        let isAccepted = false;
        let isRejected = false;
        
        if (taData.dosenAccept) {
          // If there's an acceptance record for this dosen
          const acceptRecord = taData.dosenAccept[currentUser.uid];
          if (acceptRecord) {
            if (acceptRecord.status === 'accepted') isAccepted = true;
            if (acceptRecord.status === 'rejected') isRejected = true;
          }
        }
        
        // Skip accepted or rejected assignments
        if (isAccepted || isRejected) continue;
        
        // Get mahasiswa data
        if (taData.mahasiswaId) {
          try {
            const mahasiswaDoc = await getDoc(doc(db, "users", taData.mahasiswaId));
            if (mahasiswaDoc.exists()) {
              taData.mahasiswa = mahasiswaDoc.data();
            }
          } catch (error) {
            console.error(`Error fetching mahasiswa data for ${taData.id}:`, error);
          }
        }
        
        pendingData.push(taData);
      }
      
      setPendingAssignments(pendingData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching pending assignments:", error);
      setError("Terjadi kesalahan saat mengambil data tugas");
      setLoading(false);
    }
  };
  
  const handleAcceptAssignment = async (assignment) => {
    try {
      setLoading(true);
      
      // Update tugas akhir to mark as accepted by this dosen
      const taRef = doc(db, "tugasAkhir", assignment.id);
      
      // Create or update the dosenAccept field
      const acceptData = {
        status: 'accepted',
        timestamp: new Date().toISOString(),
        dosen: currentUser.uid
      };
      
      // Create or update the dosenAccept field
      await updateDoc(taRef, {
        [`dosenAccept.${currentUser.uid}`]: acceptData
      });
      
      // Create an initial bimbingan record if it doesn't exist
      // Check if there's already a bimbingan record for this assignment
      const bimbinganQuery = query(
        collection(db, "bimbingan"),
        where("tugasAkhirId", "==", assignment.id),
        where("pembimbingId", "==", currentUser.uid)
      );
      
      const bimbinganSnapshot = await getDocs(bimbinganQuery);
      
      if (bimbinganSnapshot.empty) {
        // Create an initial bimbingan record
        // Get dosen name
        let dosenNama = "Dosen";
        const dosenDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (dosenDoc.exists()) {
          dosenNama = dosenDoc.data().nama || "Dosen";
        }
        
        // Get mahasiswa name
        let mahasiswaNama = "Mahasiswa";
        if (assignment.mahasiswaId) {
          const mahasiswaDoc = await getDoc(doc(db, "users", assignment.mahasiswaId));
          if (mahasiswaDoc.exists()) {
            mahasiswaNama = mahasiswaDoc.data().nama || "Mahasiswa";
          }
        }
        
        const bimbinganData = {
          tugasAkhirId: assignment.id,
          mahasiswaId: assignment.mahasiswaId,
          pembimbingId: currentUser.uid,
          sesi: 0, // Initial session
          judul: `Pengantar Bimbingan - ${assignment.judul || "Tugas Akhir"}`,
          fileURL: assignment.driveLink || "https://drive.google.com/",
          fileName: '',
          catatan: `Bimbingan awal untuk tugas akhir. Anda telah menerima penugasan sebagai pembimbing.`,
          status: 'reviewed', // Mark as reviewed so it shows up
          tanggal: new Date().toISOString(),
          tanggalRevisi: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 1 week from now
          reviewCatatan: `Selamat ${mahasiswaNama}, Anda telah diterima bimbingan oleh ${dosenNama}. Silakan membuat jadwal konsultasi.`,
          reviewDriveLink: '',
          tanggalReview: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          isDosenAccepted: true
        };
        
        await addDoc(collection(db, "bimbingan"), bimbinganData);
      }
      
      setSuccess(`Tugas akhir "${assignment.judul}" berhasil diterima sebagai pembimbing.`);
      fetchPendingAssignments(); // Refresh data
    } catch (error) {
      console.error("Error accepting assignment:", error);
      setError("Terjadi kesalahan saat menerima penugasan");
    } finally {
      setLoading(false);
    }
  };
  
  const handleRejectAssignment = async (assignment) => {
    try {
      if (!window.confirm("Apakah Anda yakin ingin menolak penugasan ini? Tindakan ini tidak dapat dibatalkan.")) {
        return;
      }
      
      setLoading(true);
      
      // Update tugas akhir to mark as rejected by this dosen
      const taRef = doc(db, "tugasAkhir", assignment.id);
      
      // Create or update the dosenAccept field
      const rejectData = {
        status: 'rejected',
        timestamp: new Date().toISOString(),
        dosen: currentUser.uid
      };
      
      // Update the dosenAccept field
      await updateDoc(taRef, {
        [`dosenAccept.${currentUser.uid}`]: rejectData
      });
      
      // Also remove this dosen from the pembimbingId array
      const pembimbingArray = Array.isArray(assignment.pembimbingId) 
        ? assignment.pembimbingId.filter(id => id !== currentUser.uid)
        : [];
        
      await updateDoc(taRef, { pembimbingId: pembimbingArray });
      
      setSuccess(`Penugasan tugas akhir "${assignment.judul}" berhasil ditolak.`);
      fetchPendingAssignments(); // Refresh data
    } catch (error) {
      console.error("Error rejecting assignment:", error);
      setError("Terjadi kesalahan saat menolak penugasan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title>Penugasan Pembimbing Menunggu Konfirmasi</Card.Title>
            <Link to="/mahasiswa-bimbingan">
              <Button variant="outline-primary">
                <FaArrowRight className="me-1" />
                Mahasiswa Bimbingan
              </Button>
            </Link>
          </div>
          
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          {loading ? (
            <Alert variant="info">Memuat data penugasan...</Alert>
          ) : pendingAssignments.length === 0 ? (
            <Alert variant="info">Tidak ada penugasan pembimbing yang perlu dikonfirmasi</Alert>
          ) : (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>No</th>
                  <th>Judul Tugas Akhir</th>
                  <th>Mahasiswa</th>
                  <th>Tanggal Penugasan</th>
                  <th>Link Proposal</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {pendingAssignments.map((assignment, index) => (
                  <tr key={assignment.id}>
                    <td>{index + 1}</td>
                    <td>{assignment.judul}</td>
                    <td>
                      {assignment.mahasiswa ? (
                        <>
                          {assignment.mahasiswa.nama}<br />
                          <small className="text-muted">{assignment.mahasiswa.nim}</small>
                        </>
                      ) : (
                        <span className="text-muted">Data mahasiswa tidak ditemukan</span>
                      )}
                    </td>
                    <td>{assignment.tanggal_approve ? new Date(assignment.tanggal_approve).toLocaleDateString() : '-'}</td>
                    <td>
                      {assignment.driveLink ? (
                        <a href={assignment.driveLink} target="_blank" rel="noopener noreferrer">
                          Lihat Proposal
                        </a>
                      ) : '-'}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="success" 
                          size="sm"
                          onClick={() => handleAcceptAssignment(assignment)}
                          disabled={loading}
                        >
                          <FaCheckCircle className="me-1" />
                          Terima
                        </Button>
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={() => handleRejectAssignment(assignment)}
                          disabled={loading}
                        >
                          <FaTimesCircle className="me-1" />
                          Tolak
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PendingAssignments;